import React, { useState, useEffect, useMemo, useRef } from "react";
import BLMMapHeader from "./BLMMapHeader";
import BLMMapSidebar from "./BLMMapSidebar";
import BLMMapContainer from "./BLMMapContainer";
import MapControls from "./MapControls";
import { GroqDrawerTrigger } from "./GroqDrawerTrigger";
import { HeaderErrorBoundary, SidebarErrorBoundary, MapErrorBoundary } from "./ErrorBoundary";
import { states } from '../constants/states';
import { geocodeProviders } from '../services/geocodingService';
import { 
  useBLMData, 
  useMapState, 
  useFilters, 
  usePagination, 
  useExpandedItems, 
  useSelection 
} from '../hooks/useBLMMap';
import { useHeaderContext } from "../context/HeaderContext";
import { summarizeFieldNote } from "../services/summarizeFieldNote";
import { supabase } from '../services/supabase';

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const BLMMapPage = () => {
  const { setGroqContext, setLocation: setHeaderLocation } = useHeaderContext();
  const isMobile = useIsMobile();
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showBlmPoints, setShowBlmPoints] = useState(false);
  const [showBlmNames, setShowBlmNames] = useState(false);
  const [showVendors, setShowVendors] = useState(false);
  const [showProviders, setShowProviders] = useState(false);
  const [providers, setProviders] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  
  // GroqDrawer state and refs
  const [drawerOpen, setDrawerOpen] = useState(false);
  const groqDrawerOpenRef = React.useRef(null);
  const groqSetQueryRef = React.useRef(null);
  const [vendors, setVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]); // Store all vendors
  const [mapBounds, setMapBounds] = useState(null); // Track current map bounds
  const isProcessingFieldNotesRef = useRef(false); // Track field notes processing synchronously
  
  // Add separate state for map markers (independent of search)
  const [blmFacilitiesForMap, setBlmFacilitiesForMap] = useState([]);
  const [recAreasForMap, setRecAreasForMap] = useState([]);
  const [allBlmFacilities, setAllBlmFacilities] = useState([]); // Store all BLM facilities
  const [allRecAreas, setAllRecAreas] = useState([]); // Store all rec areas
  
  // Custom hooks for state management
  const {
    searchTerm,
    setSearchTerm,
    selectedTable,
    setSelectedTable,
    selectedState,
    setSelectedState,
    activityTags,
    toggleActivityTag
  } = useFilters();
  
  const { currentPage, goToPage } = usePagination(0, 50);
  const { expandedIds, toggleExpanded } = useExpandedItems();
  
  const {
    selectedFacility,
    location,
    fieldNotes,
    selectedFieldNotesId,
    setSelectedFacility,
    setLocation,
    setFieldNotes,
    setSelectedFieldNotesId,
    selectFacility
  } = useSelection();
  
  const {
    basemap,
    showStates,
    setBasemap,
    setShowStates,
    handleBoundsChange,
    flyTo
  } = useMapState();
  
  // Enhanced bounds change handler that only filters vendors
  const handleBoundsChangeWithVendors = (bounds) => {
    setMapBounds(bounds);
    handleBoundsChange(bounds);
    
    // Only filter vendors here - BLM facilities and rec areas are handled by separate effects
    if (showVendors && allVendors.length > 0 && bounds) {
      const isPointInBounds = (lon, lat, bounds) => {
        if (!bounds) return false;
        const [[swLng, swLat], [neLng, neLat]] = [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()]
        ];
        return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
      };

      const vendorsInView = allVendors.filter(vendor => 
        isPointInBounds(vendor.longitude, vendor.latitude, bounds)
      );
      setVendors(vendorsInView);
    }
  };
  
  // BLM data fetching with memoized activity types to prevent infinite re-renders
  const activeActivityTypes = useMemo(
    () => activityTags.filter(tag => tag.active).map(tag => tag.label),
    [activityTags]
  );
  
  const { 
    data: searchResults, 
    loading: searchLoading, 
    error: searchError, 
    totalCount: searchCount 
  } = useBLMData(searchTerm, selectedTable, selectedState, currentPage, 50, activeActivityTypes);
  
  // Filter states based on search results - using original BLMCard.js whitelist
  const allowedStates = ["AK", "AZ", "CA", "CO", "ES", "ID", "MT", "NM", "NV", "OR", "UT", "WY"];
  const filteredStates = states.filter(state => allowedStates.includes(state.code));

  // Fetch all vendors once when showVendors is first enabled
  useEffect(() => {
    const fetchAllVendors = async () => {
      if (!showVendors) {
        setAllVendors([]);
        setVendors([]);
        return;
      }

      // Only fetch if we don't already have vendor data
      if (allVendors.length === 0) {
        try {
          const { data, error } = await supabase
            .from("vendor_chunks")
            .select("ID, vendor_name, state, category, content, image_urls, latitude, longitude")
            .order("ID", { ascending: false })
            .limit(500); // Get more vendors since we'll filter by bounds

          if (error) {
            // Error fetching vendors
            setAllVendors([]);
          } else {
            // Filter vendors that have valid coordinates
            const validVendors = (data || []).filter(vendor => {
              const hasCoords = vendor.latitude && vendor.longitude && 
                              !isNaN(vendor.latitude) && !isNaN(vendor.longitude);
              return hasCoords;
            });
            setAllVendors(validVendors);
            
            // If map bounds are available, filter vendors immediately
            if (mapBounds) {
              const isPointInBounds = (lon, lat, bounds) => {
                if (!bounds) return false;
                const [[swLng, swLat], [neLng, neLat]] = [
                  [bounds.getWest(), bounds.getSouth()],
                  [bounds.getEast(), bounds.getNorth()]
                ];
                return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
              };

              const vendorsInView = validVendors.filter(vendor => 
                isPointInBounds(vendor.longitude, vendor.latitude, mapBounds)
              );
              setVendors(vendorsInView);
            } else {
              // If no bounds available yet, show all vendors initially
              // They will be filtered when bounds become available
              setVendors(validVendors);
            }
          }
        } catch (err) {
          // Error fetching vendors
          setAllVendors([]);
        }
      }
    };

    fetchAllVendors();
  }, [showVendors, allVendors.length, mapBounds]);

  // Fetch and geocode outdoor recreation providers
  useEffect(() => {
    const fetchProviders = async () => {
      if (!showProviders) {
        setAllProviders([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('providers')
          .select('*');

        if (error) throw error;

        if (data && data.length > 0) {
          // Geocode providers concurrently
          const geocoded = await geocodeProviders(data);
          setAllProviders(geocoded);
        } else {
          setAllProviders([]);
        }
      } catch (err) {
        console.error('Error fetching providers:', err);
        setAllProviders([]);
      }
    };

    fetchProviders();
  }, [showProviders]);

  // Filter providers based on map bounds
  useEffect(() => {
    if (!showProviders || !mapBounds || allProviders.length === 0) {
      setProviders([]);
      return;
    }

    // Utility function to check if a point is within map bounds
    const isPointInBounds = (lon, lat, bounds) => {
      if (!bounds) return false;
      const [[swLng, swLat], [neLng, neLat]] = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()]
      ];
      return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
    };

    const providersInView = allProviders.filter(provider =>
      isPointInBounds(provider.longitude, provider.latitude, mapBounds)
    );

    setProviders(providersInView);
  }, [showProviders, mapBounds, allProviders]);

  // Filter vendors based on map bounds
  useEffect(() => {
    if (!showVendors || !mapBounds || allVendors.length === 0) {
      setVendors([]);
      return;
    }

    // Utility function to check if a point is within map bounds
    const isPointInBounds = (lon, lat, bounds) => {
      if (!bounds) return false;
      const [[swLng, swLat], [neLng, neLat]] = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()]
      ];
      return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
    };

    const vendorsInView = allVendors.filter(vendor => 
      isPointInBounds(vendor.longitude, vendor.latitude, mapBounds)
    );

    setVendors(vendorsInView);
  }, [showVendors, mapBounds, allVendors]);

  // Filter BLM facilities based on map bounds
  useEffect(() => {
    if (!showBlmPoints || !mapBounds || allBlmFacilities.length === 0) {
      setBlmFacilitiesForMap([]);
      return;
    }

    // Utility function to check if a point is within map bounds
    const isPointInBounds = (lon, lat, bounds) => {
      if (!bounds) return false;
      const [[swLng, swLat], [neLng, neLat]] = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()]
      ];
      return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
    };

    const facilitiesInView = allBlmFacilities.filter(facility => 
      isPointInBounds(facility.longitude, facility.latitude, mapBounds)
    );

    setBlmFacilitiesForMap(facilitiesInView);
  }, [showBlmPoints, mapBounds, allBlmFacilities]);

  // Filter recreation areas based on map bounds
  useEffect(() => {
    if (!showBlmNames || !mapBounds || allRecAreas.length === 0) {
      setRecAreasForMap([]);
      return;
    }

    // Utility function to check if a point is within map bounds
    const isPointInBounds = (lon, lat, bounds) => {
      if (!bounds) return false;
      const [[swLng, swLat], [neLng, neLat]] = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()]
      ];
      return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
    };

    const recAreasInView = allRecAreas.filter(recArea => 
      isPointInBounds(recArea.RecAreaLongitude, recArea.RecAreaLatitude, mapBounds)
    );

    setRecAreasForMap(recAreasInView);
  }, [showBlmNames, mapBounds, allRecAreas]);
  
  // Fetch all BLM facilities once when showBlmPoints is first enabled
  useEffect(() => {
    const fetchAllBlmFacilities = async () => {
      if (!showBlmPoints) {
        setAllBlmFacilities([]);
        setBlmFacilitiesForMap([]);
        return;
      }

      // Fetch all records using pagination to bypass Supabase limits
      if (true) { // Changed from: if (allBlmFacilities.length === 0)
        try {
          let allFacilities = [];
          let page = 0;
          const pageSize = 1000; // Fetch 1000 records per request
          let hasMore = true;

          while (hasMore) {
            const { data, error } = await supabase
              .from("blm_facilities")
              .select('*')
              .range(page * pageSize, (page + 1) * pageSize - 1);

            if (error) {
              break;
            }

            if (data && data.length > 0) {
              allFacilities = [...allFacilities, ...data];
              hasMore = data.length === pageSize; // Continue if we got a full page
              page++;
            } else {
              hasMore = false;
            }
          }

          const validFacilities = (allFacilities || []).filter(facility => 
            facility.latitude && facility.longitude && 
            !isNaN(facility.latitude) && !isNaN(facility.longitude)
          );
          setAllBlmFacilities(validFacilities);

          // If map bounds are available, filter facilities immediately
          if (mapBounds) {
            const isPointInBounds = (lon, lat, bounds) => {
              if (!bounds) return false;
              const [[swLng, swLat], [neLng, neLat]] = [
                [bounds.getWest(), bounds.getSouth()],
                [bounds.getEast(), bounds.getNorth()]
              ];
              return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
            };

            const facilitiesInView = validFacilities.filter(facility => 
              isPointInBounds(facility.longitude, facility.latitude, mapBounds)
            );
            setBlmFacilitiesForMap(facilitiesInView);
          } else {
            setBlmFacilitiesForMap(validFacilities);
          }
        } catch (err) {
          // Error fetching BLM facilities for map
          setAllBlmFacilities([]);
        }
      }
    };

    fetchAllBlmFacilities();
  }, [showBlmPoints, mapBounds]); // Removed allBlmFacilities.length dependency to force fresh fetches

  // Fetch all recreation areas once when showBlmNames is first enabled
  useEffect(() => {
    const fetchAllRecAreas = async () => {
      if (!showBlmNames) {
        setAllRecAreas([]);
        setRecAreasForMap([]);
        return;
      }

      // Only fetch if we don't already have recreation areas data
      if (allRecAreas.length === 0) {
        try {
          let allRecAreas = [];
          let page = 0;
          const pageSize = 1000; // Fetch 1000 records per request
          let hasMore = true;
          
          while (hasMore) {
            const { data, error } = await supabase
              .from("rec_areas")
              .select('*')
              .range(page * pageSize, (page + 1) * pageSize - 1);
              
            if (error) {
              // Error fetching recreation areas page
              break;
            }
            
            if (data && data.length > 0) {
              allRecAreas = [...allRecAreas, ...data];
              hasMore = data.length === pageSize; // Continue if we got a full page
              page++;
            } else {
              hasMore = false;
            }
          }
          
          if (allRecAreas.length === 0) {
            setAllRecAreas([]);
          } else {
            const validRecAreas = (allRecAreas || []).filter(recArea => 
              recArea.RecAreaLatitude && recArea.RecAreaLongitude && 
              !isNaN(recArea.RecAreaLatitude) && !isNaN(recArea.RecAreaLongitude)
            );
            setAllRecAreas(validRecAreas);
            
            // If map bounds are available, filter rec areas immediately
            if (mapBounds) {
              const isPointInBounds = (lon, lat, bounds) => {
                if (!bounds) return false;
                const [[swLng, swLat], [neLng, neLat]] = [
                  [bounds.getWest(), bounds.getSouth()],
                  [bounds.getEast(), bounds.getNorth()]
                ];
                return lon >= swLng && lon <= neLng && lat >= swLat && lat <= neLat;
              };

              const recAreasInView = validRecAreas.filter(recArea => 
                isPointInBounds(recArea.RecAreaLongitude, recArea.RecAreaLatitude, mapBounds)
              );
              setRecAreasForMap(recAreasInView);
            } else {
              // If no bounds available yet, show all rec areas initially
              setRecAreasForMap(validRecAreas);
            }
          }
        } catch (err) {
          // Error fetching recreation areas for map
          setAllRecAreas([]);
        }
      }
    };

    fetchAllRecAreas();
  }, [showBlmNames, allRecAreas.length, mapBounds]);
  
  // Handle field notes selection
  const handleFieldNotesSelect = async (id) => {
    // Set processing flag immediately to prevent any interference
    isProcessingFieldNotesRef.current = true;
    
    // Use name if id is undefined
    const facilityIdentifier = id || selectedFacility?.name;
    
    // Toggle selection - if same ID is selected again, deselect it
    if (selectedFieldNotesId === facilityIdentifier) {
      setSelectedFieldNotesId(null);
      setFieldNotes(null);
      setGroqContext("");
      // Clear location if it was set by field notes selection
      setLocation({ name: "", lat: undefined, lon: undefined });
      isProcessingFieldNotesRef.current = false;
      return;
    }

    setSelectedFieldNotesId(facilityIdentifier);

    // Look for facility in search results first, then in selected facility, then in vendors
    let facility = searchResults.find(item => (item.id === facilityIdentifier) || (item.name === facilityIdentifier));
    if (!facility && selectedFacility && ((selectedFacility.id === facilityIdentifier) || (selectedFacility.name === facilityIdentifier))) {
      facility = selectedFacility;
    }
    if (!facility && vendors && vendors.length > 0) {
      facility = vendors.find(v => (v.id === facilityIdentifier) || (v.name === facilityIdentifier));
    }

    if (facility) {
      // Check if this is a vendor - vendors have vendor_name property AND come from vendor markers
      const isVendor = facility.vendor_name && facility.vendor_name === facility.name;
      
      const note = {
        name: isVendor ? facility.vendor_name : facility.name,
        description: isVendor ? 
          `Vendor: ${facility.vendor_name}${facility.formatted_address ? ` at ${facility.formatted_address}` : ''}` : 
          facility.description,
        subtype: isVendor ? "Vendor" : facility.facility_subtype,
        latitude: facility.latitude,
        longitude: facility.longitude,
        unit_name: isVendor ? facility.formatted_address : facility.unit_name,
        state: facility.state,
        id: facility.id,
        isVendor: isVendor
      };
      
      setFieldNotes(note);
      // Update location context for header display immediately (like in BLMCard)
      setTimeout(() => {
        setHeaderLocation({
          name: note.name,
          lat: note.latitude,
          lon: note.longitude
        });
        setTimeout(() => {
          isProcessingFieldNotesRef.current = false;
        }, 100);
      }, 0);
      try {
        // Use appropriate summarization based on type
        if (isVendor) {
          const vendorSummary = `${note.name} - Vendor located at ${note.latitude}, ${note.longitude}${note.unit_name ? ` (${note.unit_name})` : ''}`;
          setGroqContext(vendorSummary);
        } else {
          const summary = await summarizeFieldNote(note);
          setGroqContext(summary);
        }
      } catch (err) {
        setGroqContext("(Summary unavailable)");
      }
    }
  };
  
  // Handle marker clicks from map
  const handleMarkerClick = (facility) => {
    // Don't process marker clicks if field notes are being processed
    if (isProcessingFieldNotesRef.current) {
      return;
    }
    // Don't clear field notes if this is the same facility that's already selected
    const isSameFacility = selectedFacility && selectedFacility.id === facility.id;
    // Check if field notes are currently active
    const areFieldNotesActive = selectedFieldNotesId !== null;
    if (!isSameFacility && !areFieldNotesActive) {
      setSelectedFieldNotesId(null);
      setFieldNotes(null);
      setGroqContext("");
    }
    // Set the clicked facility as selected
    setSelectedFacility({
      ...facility,
      expanded: selectedFacility?.expanded || false
    });
    // Only set location via marker click if field notes aren't currently active
    const latitude = facility.latitude || facility.RecAreaLatitude;
    const longitude = facility.longitude || facility.RecAreaLongitude;
    if (!isSameFacility && !areFieldNotesActive) {
      setLocation({
        name: facility.name || facility.RecAreaName,
        lat: latitude,
        lon: longitude
      });
    } else if (areFieldNotesActive) {
      // ...existing code...
    } else {
      // ...existing code...
    }
    // Open sidebar on mobile when marker is clicked
    if (isMobile) {
      setSidebarOpen(true);
    }
  };

  // Handle facility location
  const handleFacilityLocate = (facility) => {
    // Check if facility exists
    if (!facility) {
      // handleFacilityLocate: No facility provided
      return;
    }

    // Clear field notes selection when a new facility is located
    setSelectedFieldNotesId(null);
    setFieldNotes(null);
    setGroqContext("");

    // Handle both data structures
    const latitude = facility.latitude || facility.RecAreaLatitude;
    const longitude = facility.longitude || facility.RecAreaLongitude;
    
    // Do nothing if lat/long are empty, null, undefined, or 0
    if (
      latitude === null || latitude === undefined || latitude === '' || latitude === 0 ||
      longitude === null || longitude === undefined || longitude === '' || longitude === 0
    ) {

      return;
    }
    
    // Set the located facility for map marker
    setSelectedFacility({
      ...facility,
      expanded: selectedFacility?.expanded || false
    });
    
    // Update location for sidebar
    setLocation({
      name: facility.name || facility.RecAreaName,
      lat: latitude,
      lon: longitude
    });
    
    // Fly to location on map
    flyTo(facility.longitude || facility.RecAreaLongitude, facility.latitude || facility.RecAreaLatitude, 12);
    
    // Close sidebar on mobile when locating
    if (isMobile) {
      setSidebarOpen(false);
    }
  };
  
  // Handle facility expansion
  const handleFacilityExpand = (facilityId) => {
    toggleExpanded(facilityId);
    if (selectedFacility && selectedFacility.id === facilityId) {
      setSelectedFacility(prev => ({
        ...prev,
        expanded: !prev.expanded
      }));
    }
  };

  // Handle GroqDrawer opening with predefined queries
  const openGroqDrawer = (queryText) => {
    if (groqSetQueryRef.current && typeof groqSetQueryRef.current === 'function' && queryText) {
      groqSetQueryRef.current(queryText);
    }
    if (groqDrawerOpenRef.current) {
      groqDrawerOpenRef.current();
    } else {
      setDrawerOpen(true);
    }
  };
  
  return (
    <div style={{ 
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
      minHeight: "100vh", 
      background: "#f8fafc" 
    }}>
      {/* Header Component with Error Boundary */}
      <HeaderErrorBoundary>
        <BLMMapHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedTable={selectedTable}
          onTableChange={setSelectedTable}
          showBlmPoints={showBlmPoints}
          onShowBlmPointsChange={setShowBlmPoints}
          showBlmNames={showBlmNames}
          onShowBlmNamesChange={setShowBlmNames}
          isSearching={searchLoading}
        />
      </HeaderErrorBoundary>
      
      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setSidebarOpen(open => !open)}
        style={{
          position: 'fixed',
          top: `calc(50vh - 20px)`,
          left: sidebarOpen && !isMobile ? 410 : 16,
          zIndex: 1100,
          background: 'white',
          border: '2px solid #000',
          borderRadius: 8,
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'left 0.3s',
          cursor: 'pointer'
        }}
        aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {sidebarOpen ? (
            <path d="M15 6L9 12L15 18" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M9 6L15 12L9 18" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      </button>

      {/* Main Container */}
      <div style={{ display: "flex", height: "calc(100vh - 65px)", position: 'relative' }}>
        {/* Sidebar Component with Error Boundary */}
        {sidebarOpen && (
          <SidebarErrorBoundary>
            <BLMMapSidebar
              isOpen={sidebarOpen}
              isMobile={isMobile}
              selectedTable={selectedTable}
              searchTerm={searchTerm}
              searchResults={searchResults}
              searchLoading={searchLoading}
              searchError={searchError}
              searchCount={searchCount}
              currentPage={currentPage}
              resultsPerPage={50}
              onPageChange={goToPage}
              selectedFacility={selectedFacility}
              fieldNotes={fieldNotes}
              location={location}
              selectedFieldNotesId={selectedFieldNotesId}
              onFieldNotesSelect={handleFieldNotesSelect}
              onFacilityLocate={handleFacilityLocate}
              onFacilityExpand={handleFacilityExpand}
              expandedIds={expandedIds}
              selectedState={selectedState}
              onStateChange={setSelectedState}
              filteredStates={filteredStates}
              activityTags={activityTags}
              onActivityTagToggle={toggleActivityTag}
            />
          </SidebarErrorBoundary>
        )}

        {/* Map Container Component with Error Boundary */}
        <MapErrorBoundary>
          <BLMMapContainer
            basemap={basemap}
            showStates={showStates}
            locatedBlm={selectedFacility}
            vendorMarkersInView={vendors}
            blmFacilitiesInView={blmFacilitiesForMap}
            recAreaMarkersInView={recAreasForMap}
            providersInView={providers}
            onLocationSelect={setLocation}
            onMarkerClick={handleMarkerClick}
            onMapBoundsChange={handleBoundsChangeWithVendors}
            showBlmPoints={showBlmPoints}
            showBlmNames={showBlmNames}
            showVendors={showVendors}
            showProviders={showProviders}
          />
        </MapErrorBoundary>

        {/* Floating Map Controls - positioned as sibling to avoid wrapper interference */}
        <MapControls
          showBlmPoints={showBlmPoints}
          onShowBlmPointsChange={setShowBlmPoints}
          showBlmNames={showBlmNames}
          onShowBlmNamesChange={setShowBlmNames}
          showVendors={showVendors}
          onShowVendorsChange={setShowVendors}
          showProviders={showProviders}
          onShowProvidersChange={setShowProviders}
          basemap={basemap}
          onBasemapChange={setBasemap}
          isMobile={isMobile}
          blmPointsCount={blmFacilitiesForMap.length}
          blmNamesCount={recAreasForMap.length}
          vendorsCount={vendors.length}
          providersCount={providers.length}
        />
      </div>
      
      {/* Yogi Assistant (GroqDrawerTrigger) */}
      <GroqDrawerTrigger
        onOpen={fn => (groqDrawerOpenRef.current = fn)}
        setQuery={fn => (groqSetQueryRef.current = fn)}
        openDrawer={drawerOpen}
        systemPromptKey="blm_map_system_prompt"
      />
    </div>
  );
};

export default BLMMapPage;
